import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { UIProvider, useUI } from "@/context/UIContext";
import { Loader } from "@/components/Loader";
import { CustomCursor } from "@/components/CustomCursor";
import { ScrollProgress } from "@/components/ScrollProgress";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EnquiryModal } from "@/components/EnquiryModal";
import Home from "@/pages/Home";
import Concierge from "@/pages/Concierge";
import Tickets from "@/pages/Tickets";
import Events from "@/pages/Events";
import Membership from "@/pages/Membership";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function SmoothScroll({ children }) {
  useEffect(() => {
    if (prefersReduced()) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SiteChrome() {
  const location = useLocation();
  const { setLoaderDone } = useUI();
  const [showLoader, setShowLoader] = useState(() => {
    try {
      return !sessionStorage.getItem("mds_visited");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!showLoader) setLoaderDone(true);
  }, [showLoader, setLoaderDone]);

  const finishLoader = () => {
    try {
      sessionStorage.setItem("mds_visited", "1");
    } catch {
      /* private mode */
    }
    setShowLoader(false);
    setLoaderDone(true);
  };

  const bare = location.pathname === "/login";

  return (
    <div className="App">
      <div className="grain-overlay" aria-hidden="true" />
      <CustomCursor />
      <ScrollProgress />
      <AnimatePresence>
        {showLoader && <Loader key="loader" onComplete={finishLoader} />}
      </AnimatePresence>
      {!bare && <Navbar />}
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home />} />
          <Route path="/concierge" element={<Concierge />} />
          <Route path="/tickets" element={<Tickets />} />
          <Route path="/events" element={<Events />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </AnimatePresence>
      {!bare && <Footer />}
      <EnquiryModal />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#111111",
            border: "1px solid rgba(200,164,93,0.3)",
            color: "#F3EBDD",
            borderRadius: "2px",
            fontSize: "13px",
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UIProvider>
          <SmoothScroll>
            <SiteChrome />
          </SmoothScroll>
        </UIProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
